import {
  ClockDto,
  ClockingDayDto,
  ClockingDayLimitDto,
  ClockingDto,
  ClockingMonthDto,
  ClockingRegistryDto
} from '@app/data/dtos/mot/clocking/clocking.dto';
import { IDataResponse } from '@app/entities/interfaces/data/data.interface';
import {
  Clock,
  Clocking,
  ClockingDay,
  ClockingDayLimit,
  ClockingMonth,
  ClockingRegistry,
  ClockType,
  DayClockingType
} from '@app/entities/models/mot/clocking/clocking.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

export const mapClocking: MapFromFn<ClockingDto, Clocking> = (value) =>
  Builder.forModel(Clocking).lastUpdate(value.last_update).months(value.months.map(mapClockingMonth)).build();

export const mapClockingMonth: MapFromFn<ClockingMonthDto, ClockingMonth> = (value) =>
  Builder.forModel(ClockingMonth).month(value.month).days(value.days.map(mapClockingDay)).build();

const isDayClockingType = (value: string): value is DayClockingType =>
  Object.values<string>(DayClockingType).includes(value);

export const mapDayClockingType = (value?: string): DayClockingType =>
  value && isDayClockingType(value) ? value : DayClockingType.NORMAL;

const mapDayLimit = (value?: ClockingDayLimitDto | null): ClockingDayLimit | null =>
  value ? { time: value.time, date: value.date } : null;

const countRegistries = (clocks: Clock[]): number =>
  clocks.reduce((total, clock) => total + (clock.entry ? 1 : 0) + (clock.exit ? 1 : 0), 0);

export const mapClockingDay: MapFromFn<ClockingDayDto, ClockingDay> = (value) => {
  const clocks = value.clocks.map(mapClock);

  return Builder.forModel(ClockingDay)
    .date(value.date)
    .label(value.label)
    .hasError(value.day_clocking_error)
    .clocks(clocks)
    .dayClockingType(mapDayClockingType(value.day_clocking_type))
    .numOfClockings(value.num_of_clockings ?? countRegistries(clocks))
    .firstEntry(mapDayLimit(value.first_entry))
    .lastExit(mapDayLimit(value.last_exit))
    .build();
};

const mapClockTransfer = (value: ClockingRegistryDto): ClockingRegistry => ({
  time: value.time,
  type: value.type === 'man' ? ClockType.MAN : ClockType.CLK,
  date: value.date
});

export const mapClock: MapFromFn<ClockDto, Clock> = (value) =>
  Builder.forModel(Clock)
    .entry(value.entry ? mapClockTransfer(value.entry) : null)
    .exit(value.exit ? mapClockTransfer(value.exit) : null)
    .build();

export const mapClockingData: MapFromFn<IDataResponse<ClockingDto>, Clocking> = (value) => mapClocking(value.data);
